// Libraries
import { z } from 'zod';
// Utils
import { OptimizedSpeedsAsString, isValidNumber, roundToStep } from '@helpers/index';
import text from '@translations/en.json';
// Types
import { RatingKeys } from '@globalTypes/index';
import { CiiRatingSchema, FuelMode, FuelOption, FuelOptionSchema, ParametersRanges } from '@schemas/services';

export type CiiSimulatorFormValues = {
  fuelType: FuelOption;
  fuelPrice: string;
  carbonPrice: string;
  ladenAvgSpeed: string;
  ballastAvgSpeed: string;
  sailingDays: string;
  avgBeaufort: string;
  optimizedSpeeds: OptimizedSpeedsAsString;
  uwc: boolean;
  ciiRating?: RatingKeys;
  canOptimize: number;
  fuelModes: FuelMode[];
  bulkFuelType?: FuelOption;
  bulkFuelPrice?: string;
  bulkFuelQuantity?: string;
  bulkFuelEnabled: boolean;
};

const buildStringRangeSchema = (min: number, max: number) =>
  z
    .string({ required_error: text.value_is_required })
    .refine((val) => isValidNumber(val), {
      message: text.invalid_number,
    })
    // TODO: Get this values from the BE
    .refine((val) => Number(val) > min && Number(val) <= max, {
      message: `${text.range_is} ${min} - ${max}`,
    });

export const CiiSimulatorFormSchemaGenerator = (parametersRanges: ParametersRanges) =>
  z
    .object({
      fuelType: FuelOptionSchema,
      bulkFuelType: FuelOptionSchema.optional(),
      bulkFuelEnabled: z.boolean(),
      fuelPrice: buildStringRangeSchema(0, 10000),
      bulkFuelPrice: z.string().optional(),
      bulkFuelQuantity: z.string().optional(),
      carbonPrice: z
        .string({ required_error: text.value_is_required })
        .refine((val) => isValidNumber(val), {
          message: text.invalid_number,
        })
        .refine(
          (val) =>
            Number(val) >= parametersRanges.carbonCredit.minValue &&
            Number(val) <= parametersRanges.carbonCredit.maxValue,
          {
            message: `${text.range_is} ${parametersRanges.carbonCredit.minValue} - ${parametersRanges.carbonCredit.maxValue}`,
          }
        ),
      ladenAvgSpeed: z
        .string({ required_error: text.value_is_required })
        .refine((val) => isValidNumber(val), {
          message: text.invalid_number,
        })
        .refine(
          (val) =>
            Number(val) >= parametersRanges.averageSpeed.minValue &&
            Number(val) <= parametersRanges.averageSpeed.maxValue,
          {
            message: `${text.range_is} ${roundToStep(parametersRanges.averageSpeed.minValue, 0.1)} - ${roundToStep(
              parametersRanges.averageSpeed.maxValue,
              0.1
            )}`,
          }
        ),
      ballastAvgSpeed: z
        .string({ required_error: text.value_is_required })
        .refine((val) => isValidNumber(val), {
          message: text.invalid_number,
        })
        .refine(
          (val) =>
            Number(val) >= parametersRanges.averageSpeed.minValue &&
            Number(val) <= parametersRanges.averageSpeed.maxValue,
          {
            message: `${text.range_is} ${roundToStep(parametersRanges.averageSpeed.minValue, 0.1)} - ${roundToStep(
              parametersRanges.averageSpeed.maxValue,
              0.1
            )}`,
          }
        ),
      sailingDays: z
        .string({ required_error: text.value_is_required })
        .refine((val) => isValidNumber(val), {
          message: text.invalid_number,
        })
        .refine(
          (val) =>
            Number(val) >= parametersRanges.daysSailingPerMonth.minValue &&
            Number(val) <= parametersRanges.daysSailingPerMonth.maxValue,
          {
            message: `${text.range_is} ${parametersRanges.daysSailingPerMonth.minValue} - ${parametersRanges.daysSailingPerMonth.maxValue}`,
          }
        ),
      avgBeaufort: z
        .string({ required_error: text.value_is_required })
        .refine((val) => isValidNumber(val), {
          message: text.invalid_number,
        })
        .refine(
          (val) =>
            Number(val) >= parametersRanges.averageBeaufortScale.minValue &&
            Number(val) <= parametersRanges.averageBeaufortScale.maxValue,
          {
            message: `${text.range_is} ${parametersRanges.averageBeaufortScale.minValue} - ${parametersRanges.averageBeaufortScale.maxValue}`,
          }
        ),
      optimizedSpeeds: z.object({
        A: z.object({
          ballastSpeedKts: z.string(),
          ladenSpeedKts: z.string(),
        }),
        B: z.object({
          ballastSpeedKts: z.string(),
          ladenSpeedKts: z.string(),
        }),
        C: z.object({
          ballastSpeedKts: z.string(),
          ladenSpeedKts: z.string(),
        }),
        D: z.object({
          ballastSpeedKts: z.string(),
          ladenSpeedKts: z.string(),
        }),
        E: z.object({
          ballastSpeedKts: z.string(),
          ladenSpeedKts: z.string(),
        }),
      }),
      uwc: z.boolean(),
      ciiRating: CiiRatingSchema,
      canOptimize: z.number(),
      fuelModes: z
        .array(
          z.object({
            mode: z.string(),
            percentage: z.string().optional(),
            fuels: z.array(
              z.object({
                name: z.string(),
                displayName: z.string().optional(),
                category: z.string().optional(),
                costMTUsd: z.string().optional(),
                proportion: z.string().optional(),
                conversionFactor: z.string().optional(),
              })
            ),
          })
        )
        .optional()
        .superRefine((fuelModes, ctx) => {
          fuelModes?.forEach(({ percentage, fuels }, modeIndex) => {
            const isValid = isValidNumber(percentage);
            const isInRange = Number(percentage) >= 0 && Number(percentage) <= 100;
            const isNotCero = isValid && Number(percentage) !== 0;
            if (percentage === undefined) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [`${modeIndex}`, 'percentage'],
                message: text.value_is_required,
              });
            } else if (!isValid) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [`${modeIndex}`, 'percentage'],
                message: text.invalid_number,
              });
            } else if (!isInRange) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [`${modeIndex}`, 'percentage'],
                message: `${text.range_is} 0 - 100`,
              });
            }

            if (isNotCero) {
              fuels?.forEach(({ costMTUsd }, fuelIndex) => {
                const isValid = isValidNumber(costMTUsd);
                const isInRange = Number(costMTUsd) >= 0 && Number(costMTUsd) <= 10000;
                if (costMTUsd === undefined) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [`${modeIndex}`, 'fuels', `${fuelIndex}`, 'costMTUsd'],
                    message: text.value_is_required,
                  });
                } else if (!isValid) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [`${modeIndex}`, 'fuels', `${fuelIndex}`, 'costMTUsd'],
                    message: text.invalid_number,
                  });
                } else if (!isInRange) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [`${modeIndex}`, 'fuels', `${fuelIndex}`, 'costMTUsd'],
                    message: `${text.range_is} 0 - 10000`,
                  });
                }
              });
            }
          });
        }),
    })
    .superRefine((val, ctx) => {
      if (!!val.bulkFuelEnabled) {
        const bulkFuelPriceResult = buildStringRangeSchema(0, 10000).safeParse(val.bulkFuelPrice);
        const bulkFuelQuantityResult = buildStringRangeSchema(0, 50000).safeParse(val.bulkFuelQuantity);

        if (!val.bulkFuelType) {
          ctx.addIssue({
            message: 'Select a fuel type',
            path: ['bulkFuelType'],
            code: 'custom',
          });
        }
        if (!bulkFuelPriceResult.success) {
          bulkFuelPriceResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['bulkFuelPrice'] }));
        }

        if (!bulkFuelQuantityResult.success) {
          bulkFuelQuantityResult.error?.issues.forEach((issue) =>
            ctx.addIssue({ ...issue, path: ['bulkFuelQuantity'] })
          );
        }
      }
    });
